// src/components/artwork/ZipImportButton.jsx
//
// Import d'un dossier ZIP sur une fiche existante.
// — Étape 1 : décompresse le ZIP côté client (JSZip)
// — Étape 2 : upload images → Supabase Storage artworks-images
//             upload PDFs  → Supabase Storage artworks-documents (bucket public)
// — Étape 3 (optionnel) : enrichissement IA via GPT-4o Vision API
//
// Branchement dans ArtworkEdit.jsx :
//   import ZipImportButton from "../components/artwork/ZipImportButton";
//   // Dans le rendu de l'onglet documents :
//   {activeTab === "documents" && (
//     <>
//       {canEdit && (
//         <ZipImportButton
//           artworkId={artworkId}
//           formData={formData}
//           setFormData={setFormData}
//         />
//       )}
//       <ArtworkDocumentsTab ... />
//     </>
//   )}

import React, { useRef, useState } from "react";
import { Upload, CheckCircle, AlertCircle, Loader2, Sparkles, X, FileText, Image as ImageIcon } from "lucide-react";
import { supabase } from "../../lib/supabaseClient";
import AiDisclosure from "../AiDisclosure";
import { VISION_API_URL } from "../../config/domains";

// Types de documents détectés par nom de fichier
const DOCUMENT_TYPE_RULES = [
  { pattern: /certif|authenticit|auth/i,      type: "certificate",      label: "Certificat d'authenticité" },
  { pattern: /facture|invoice|achat|receipt/i, type: "invoice",          label: "Facture" },
  { pattern: /constat|conservation|condition/i,type: "condition_report", label: "Rapport de conservation" },
  { pattern: /exposition|exhibit|galerie/i,    type: "exhibition",       label: "Document d'exposition" },
  { pattern: /donation|don\b|legs/i,           type: "other",            label: "Donation / legs" },
  { pattern: /bio|cv\b|curriculum/i,           type: "other",            label: "Biographie artiste" },
  { pattern: /photo|image|jpg|jpeg|png/i,      type: "photo",            label: "Photographie HD" },
];

function guessDocType(filename) {
  const name = filename.toLowerCase();
  for (const rule of DOCUMENT_TYPE_RULES) {
    if (rule.pattern.test(name)) return { type: rule.type, label: rule.label };
  }
  return { type: "other", label: "Autre" };
}

function isImageFile(filename) {
  return /\.(jpe?g|png|webp|gif|tiff?)$/i.test(filename);
}

function isPdfFile(filename) {
  return /\.pdf$/i.test(filename);
}

// ─── Composant principal ───────────────────────────────────────────────────

export default function ZipImportButton({ artworkId, formData, setFormData }) {
  const fileInputRef = useRef(null);
  const [step, setStep]         = useState("idle"); // idle | parsing | uploading | enriching | done | error
  const [progress, setProgress] = useState({ current: 0, total: 0, label: "" });
  const [preview, setPreview]   = useState(null);  // { images: [], docs: [] } avant confirmation
  const [errorMsg, setErrorMsg] = useState("");
  const [enrichEnabled, setEnrichEnabled] = useState(false);

  // ── 1. Sélection du ZIP ──────────────────────────────────────────────────
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    setStep("parsing");
    setErrorMsg("");
    setPreview(null);

    try {
      const JSZip = (await import("jszip")).default;
      const zip = await JSZip.loadAsync(file);

      const images = [];
      const docs   = [];

      zip.forEach((relativePath, zipEntry) => {
        if (zipEntry.dir) return;
        const filename = relativePath.split("/").pop();
        if (!filename || filename.startsWith(".")) return;

        if (isImageFile(filename)) {
          images.push({ relativePath, filename, zipEntry });
        } else if (isPdfFile(filename)) {
          const { type, label } = guessDocType(filename);
          docs.push({ relativePath, filename, zipEntry, type, label });
        }
      });

      if (images.length === 0 && docs.length === 0) {
        setErrorMsg("Aucun fichier image (JPG, PNG) ou PDF trouvé dans ce ZIP.");
        setStep("error");
        return;
      }

      setPreview({ images, docs, zipObj: zip });
      setStep("preview");

    } catch (err) {
      console.error("[ZipImport] Erreur parsing ZIP:", err);
      setErrorMsg("Impossible de lire ce fichier ZIP.");
      setStep("error");
    }
  };

  // ── 2. Confirmation et upload ────────────────────────────────────────────
  const handleConfirmImport = async () => {
    if (!preview || !artworkId) return;
    const { images, docs } = preview;
    const total = images.length + docs.length;

    setStep("uploading");
    setProgress({ current: 0, total, label: "Préparation…" });

    try {
      // — Upload images → artworks-images bucket ——————————————————————————
      const uploadedImages = [];
      for (let i = 0; i < images.length; i++) {
        const { filename, zipEntry } = images[i];
        setProgress({ current: i + 1, total, label: `Image : ${filename}` });

        const blob = await zipEntry.async("blob");
        const ext  = filename.split(".").pop();
        const path = `${artworkId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

        const { error: upErr } = await supabase.storage
          .from("artworks-images")
          .upload(path, blob, { upsert: true, contentType: blob.type || "image/jpeg" });

        if (upErr) { console.warn(`[ZipImport] image ${filename}:`, upErr); continue; }

        const { data: pub } = supabase.storage.from("artworks-images").getPublicUrl(path);
        uploadedImages.push({
          id:        `img_${Date.now()}_${Math.random().toString(36).slice(2)}`,
          path:      pub.publicUrl,
          url:       pub.publicUrl,
          publicUrl: pub.publicUrl,
          isPrimary: false,
          filename,
        });
      }

      // — Upload PDFs → artworks-documents bucket ——————————————————————————
      const uploadedDocs = [];
      for (let i = 0; i < docs.length; i++) {
        const { filename, zipEntry, type, label } = docs[i];
        setProgress({ current: images.length + i + 1, total, label: `Document : ${filename}` });

        const blob = await zipEntry.async("blob");
        const path = `${artworkId}/${Date.now()}-${Math.random().toString(36).slice(2)}.pdf`;

        const { error: upErr } = await supabase.storage
          .from("artworks-documents")
          .upload(path, blob, { upsert: true, contentType: "application/pdf" });

        if (upErr) { console.warn(`[ZipImport] doc ${filename}:`, upErr); continue; }

        const { data: pub } = supabase.storage.from("artworks-documents").getPublicUrl(path);
        uploadedDocs.push({
          id:   `doc_${Date.now()}_${Math.random().toString(36).slice(2)}`,
          name:  filename.replace(/\.pdf$/i, ""),
          type,
          label,
          file:  pub.publicUrl,
          date:  new Date().toISOString(),
        });
      }

      // — Fusionner avec l'existant ————————————————————————————————————————
      const currentImages = Array.isArray(formData?.images) ? formData.images : [];
      const currentDocs   = Array.isArray(formData?.documents) ? formData.documents : [];

      // Première image = principale si aucune n'existe
      let newImages = [...uploadedImages];
      const hasExistingPrimary = currentImages.some(img => img.isPrimary);
      if (!hasExistingPrimary && newImages.length > 0) newImages[0].isPrimary = true;

      const allImages = [...currentImages, ...newImages];
      const allDocs   = [...currentDocs,   ...uploadedDocs];

      // — Sauvegarder en base ——————————————————————————————————————————————
      setProgress({ current: total, total, label: "Sauvegarde en base…" });

      const { error: saveErr } = await supabase
        .from("artworks")
        .update({
          images:    allImages,   // jsonb — objet natif
          documents: JSON.stringify(allDocs),
          image_url: allImages.find(img => img.isPrimary)?.url || allImages[0]?.url || null,
        })
        .eq("id", artworkId);

      if (saveErr) throw saveErr;

      // — Mettre à jour le state React ——————————————————————————————————————
      setFormData(prev => ({
        ...prev,
        images:    allImages,
        documents: allDocs,
        image_url: allImages.find(img => img.isPrimary)?.url || allImages[0]?.url || null,
      }));

      // — Enrichissement IA (optionnel) ————————————————————————————————————
      if (enrichEnabled && uploadedDocs.length > 0) {
        await runAiEnrichment(uploadedDocs, allImages);
      }

      setStep("done");

    } catch (err) {
      console.error("[ZipImport] Erreur upload:", err);
      setErrorMsg("Erreur lors de l'upload. Vérifiez la console.");
      setStep("error");
    }
  };

  // ── 3. Enrichissement IA ─────────────────────────────────────────────────
  const runAiEnrichment = async (docs, images) => {
    setStep("enriching");
    setProgress({ current: 0, total: docs.length, label: "Analyse IA en cours…" });

    try {
      // Appel à ton API Vision existante (main.py port 8000)
      // On envoie les URLs des PDFs pour extraction GPT-4o
      const pdfUrls = docs.map(d => d.file);
      const imgUrls = images.slice(0, 3).map(i => i.url); // max 3 images

      const res = await fetch(`${VISION_API_URL}/enrich-from-documents`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          artwork_id: artworkId,
          pdf_urls:   pdfUrls,
          image_urls: imgUrls,
        }),
      });

      if (!res.ok) {
        console.warn("[ZipImport] Enrichissement IA échoué:", res.status);
        return; // non bloquant
      }

      const enriched = await res.json();

      // Patcher formData avec les champs retournés par l'IA
      if (enriched && typeof enriched === "object") {
        const patch = {};
        const fields = ["title", "artist", "year", "medium", "description",
                        "dimensions", "inventory_number", "condition"];
        for (const f of fields) {
          if (enriched[f] && !formData?.[f]) patch[f] = enriched[f];
        }

        if (Object.keys(patch).length > 0) {
          await supabase.from("artworks").update(patch).eq("id", artworkId);
          setFormData(prev => ({ ...prev, ...patch }));
        }
      }
    } catch (err) {
      console.warn("[ZipImport] Enrichissement IA non bloquant:", err);
    }
  };

  // ── Reset ─────────────────────────────────────────────────────────────────
  const handleReset = () => {
    setStep("idle");
    setPreview(null);
    setErrorMsg("");
    setProgress({ current: 0, total: 0, label: "" });
  };

  // ── Rendu ─────────────────────────────────────────────────────────────────
  return (
    <div className="mb-6">

      {/* ── BOUTON DÉCLENCHEUR ── */}
      {step === "idle" && (
        <>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:border-black hover:text-black transition bg-white"
          >
            <Upload className="w-4 h-4" />
            Importer un dossier ZIP
          </button>
          <p className="mt-1.5 text-xs text-gray-400">
            Images (JPG, PNG) + documents (PDF) — un sous-dossier de ta collection
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip"
            className="hidden"
            onChange={handleFileSelect}
          />
        </>
      )}

      {/* ── PARSING ── */}
      {step === "parsing" && (
        <div className="flex items-center gap-3 p-4 border border-gray-200 rounded-xl bg-gray-50">
          <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
          <span className="text-sm text-gray-600">Lecture du ZIP…</span>
        </div>
      )}

      {/* ── PRÉVISUALISATION ── */}
      {step === "preview" && preview && (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
            <span className="text-sm font-medium text-gray-900">
              Contenu détecté dans le ZIP
            </span>
            <button type="button" onClick={handleReset}>
              <X className="w-4 h-4 text-gray-400 hover:text-gray-700" />
            </button>
          </div>

          <div className="p-4 space-y-4">
            {/* Images */}
            {preview.images.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <ImageIcon className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">
                    {preview.images.length} image{preview.images.length > 1 ? "s" : ""}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {preview.images.map(({ filename }) => (
                    <span key={filename} className="px-2 py-0.5 rounded bg-gray-100 text-xs text-gray-600">
                      {filename}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Documents */}
            {preview.docs.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">
                    {preview.docs.length} document{preview.docs.length > 1 ? "s" : ""}
                  </span>
                </div>
                <div className="space-y-1">
                  {preview.docs.map(({ filename, label }) => (
                    <div key={filename} className="flex items-center justify-between text-xs">
                      <span className="text-gray-700">{filename}</span>
                      <span className="text-gray-400 italic">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Option enrichissement IA */}
            <label className="flex items-start gap-3 p-3 rounded-lg border border-dashed border-gray-300 cursor-pointer hover:border-black transition">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={enrichEnabled}
                onChange={e => setEnrichEnabled(e.target.checked)}
              />
              <div>
                <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  Enrichissement IA après import
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  L'IA lira les PDFs pour compléter les champs vides de la fiche (titre, artiste, date, technique…)
                </p>
              </div>
            </label>
            {enrichEnabled && (
              <AiDisclosure
                type="extracted"
                className="mt-2"
                note="Les champs complétés par l'IA à partir des documents sont à vérifier avant de valider la fiche."
              />
            )}

            {/* Boutons */}
            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={handleConfirmImport}
                className="px-4 py-2 rounded-lg bg-black text-white text-sm font-medium hover:opacity-90 transition"
              >
                Importer ({preview.images.length + preview.docs.length} fichiers)
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── UPLOAD EN COURS ── */}
      {(step === "uploading" || step === "enriching") && (
        <div className="border border-gray-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-gray-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">
                {step === "enriching" ? "Analyse IA…" : "Import en cours…"}
              </p>
              <p className="text-xs text-gray-500 truncate">{progress.label}</p>
            </div>
          </div>
          {progress.total > 0 && (
            <div className="w-full bg-gray-100 rounded-full h-1.5">
              <div
                className="bg-black h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${Math.round((progress.current / progress.total) * 100)}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* ── SUCCÈS ── */}
      {step === "done" && (
        <div className="flex items-center justify-between p-4 border border-emerald-200 rounded-xl bg-emerald-50">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-emerald-900">Import terminé</p>
              <p className="text-xs text-emerald-700">
                Images et documents ajoutés à la fiche.
                {enrichEnabled ? " Métadonnées complétées par l'IA." : ""}
              </p>
            </div>
          </div>
          <button type="button" onClick={handleReset} className="text-xs text-emerald-700 underline">
            Importer un autre dossier
          </button>
        </div>
      )}

      {/* ── ERREUR ── */}
      {step === "error" && (
        <div className="flex items-center justify-between p-4 border border-red-200 rounded-xl bg-red-50">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">{errorMsg}</p>
          </div>
          <button type="button" onClick={handleReset} className="text-xs text-red-700 underline">
            Réessayer
          </button>
        </div>
      )}
    </div>
  );
}